import React from "react";
import { Phone, MessageCircle, ShieldCheck, Flame, SearchCheck, Sparkles, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
const inspectionFeatures = [{
  icon: Flame,
  title: "ضبط ومعايرة الشعلة",
  description: "معايرة تدفق الغاز وتنظيم الشعلة لتكون زرقاء متوازنة وقوية دون هباب أو سخام أسود."
}, {
  icon: SearchCheck,
  title: "فحص تسريب الغاز بالأجهزة",
  description: "كشف دقيق لمنظومة ومواسير الغاز وصمامات الأمان بأحدث أجهزة كشف التسرب المعتمدة."
}, {
  icon: ShieldCheck,
  title: "فحص وتأمين الوصلات والصمامات",
  description: "معاينة شاملة لكافة الليات، الوصلات، ومنظمات الضغط واستبدال التالف منها فوراً."
}, {
  icon: Sparkles,
  title: "تنظيف وتسليك العيون والمحابس",
  description: "إزالة الدهون المتراكمة والانسدادات العميقة لضمان اشتعال سريع وتوزيع حراري مثالي."
}];
export default function GasOvenHero() {
  return <section id="gasovenmaintenancehero" data-nav="light" dir="rtl" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Hero Top Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16 lg:mb-24">
          <div className="lg:col-span-7 flex flex-col items-start gap-6">
            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/10 px-3 py-1 text-sm font-medium rounded-full">
              خدمة منزلية فورية داخل الرياض
            </Badge>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.25]">
              صيانة الأفران الغازية بالرياض وفحص شامل لسلامة أجهزة الطهي
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              نقدم في شركة لمسة احتراف خدمات صيانة الأفران الغازية بالرياض بمستوى فني متقدم. نفحص تسريب الغاز، ونعالج ضعف الشعلة، ونسلك العيون المسدودة مع تأمين الوصلات لضمان أقصى درجات الأمان في منزلك.
            </p>

            {/* Main Action Buttons */}
            <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 text-base shadow-sm">
                <a href={PHONE_TEL_HREF} className="inline-flex items-center gap-2">
                  <Phone className="size-5" />
                  <span>اتصل الآن: {PHONE_LOCAL}</span>
                </a>
              </Button>

              <Button asChild size="lg" variant="secondary" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 px-6 text-base shadow-sm">
                <Link to={WHATSAPP_HREF} newTab className="inline-flex items-center gap-2">
                  <MessageCircle className="size-5 text-emerald-400" />
                  <span>واتساب مباشر</span>
                </Link>
              </Button>
            </div>

            {/* Quick trust metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4 border-t border-border/80 w-full text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>فحص كشف التسرب</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>قطع غيار أصلية</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>فنيون متخصصون بالرياض</span>
              </div>
            </div>
          </div>

          {/* Right Visual Image */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-lg bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/gas_oven_maintenance_hero.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/gas_oven_maintenance_hero.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/gas_oven_maintenance_hero.webp-wvc-sizes" alt="فني صيانة الأفران الغازية يفحص شعلة الفرن في الرياض" loading="lazy" decoding="async" className="w-full h-auto object-cover aspect-4/3" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 right-4 left-4 p-4 rounded-lg bg-card/90 backdrop-blur-xs border border-border text-card-foreground">
                <p className="font-semibold text-sm">فحص السلامة المنزلية للأفران</p>
                <p className="text-xs text-muted-foreground mt-0.5">صيانة دورية وحلول فورية للأفران السطحية والبلت إن</p>
              </div>
            </div>
          </div>
        </div>

        {/* Feature & Inspection Cards */}
        <div className="mb-16 lg:mb-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              خدمات فحص وصيانة أفران الغاز المتكاملة
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              خطوات فنية دقيقة نعتمدها خلال زيارة الصيانة لضمان أداء تشغيلي مستقر وتأمين تام لمنزلك.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {inspectionFeatures.map((item, i) => {
            const IconComp = item.icon;
            return <Card key={item.title} data-index={i} className="border border-border bg-card shadow-xs hover:border-primary/40 transition-colors">
                  <CardHeader className="pb-3">
                    <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-2">
                      <IconComp className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Bottom CTA Block */}
        <div className="rounded-2xl bg-secondary text-secondary-foreground p-8 md:p-12 relative overflow-hidden border border-border shadow-md">
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-right">
              <h2 className="text-2xl sm:text-3xl font-bold">
                هل تلاحظ ضعفاً في الشعلة أو رائحة غاز؟
              </h2>
              <p className="text-secondary-foreground/80 max-w-xl text-sm sm:text-base">
                فريق لمسة احتراف مستعد للوصول إليك في أي حي داخل الرياض لإجراء فحص أمان فوري وإصلاح أعطال الأفران الغازية في نفس اليوم.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6 shadow-sm">
                <a href={PHONE_TEL_HREF} className="inline-flex items-center gap-2">
                  <Phone className="size-4" />
                  <span>اتصال مباشر</span>
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-secondary-foreground/30 bg-transparent text-secondary-foreground hover:bg-secondary-foreground/10 h-11 px-6">
                <Link to={WHATSAPP_HREF} newTab className="inline-flex items-center gap-2">
                  <MessageCircle className="size-4" />
                  <span>مراسلة واتساب</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}